import { useRef, useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../../shared/makeApiCall";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import AddInventoryForm from "../../../components/forms/setup/product/AddInventoryForm";
import QbProductLookupPanel from "../../../components/forms/setup/product/QbProductLookupPanel";
import ImportMediaCsvDropzone from "../../../components/importMedia/ImportMediaCsvDropzone";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

export const PRODUCT_VIEWS = {
  active: "active",
  deleted: "deleted",
};

export function ProductsViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({
    data: [],
  });
  const [productCounts, setProductCounts] = useState({
    active: 0,
    deleted: 0,
  });
  const [restoringProductId, setRestoringProductId] = useState(null);

  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [qbLookupLoadingProductId, setQbLookupLoadingProductId] =
    useState(null);
  const [productsForDropdown, setProductsForDropdown] = useState([]);
  const lastFetchRef = useRef({
    search: null,
    page: 1,
    view: PRODUCT_VIEWS.active,
  });

  const fetchProductsForDropdown = async function (search, page) {
    let callResult = await makePostApiCall(`fetchProductsForDropdown`);
    if (callResult.success) {
      setProductsForDropdown(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const refreshProducts = () => {
    const { search, page, view } = lastFetchRef.current;
    getProducts(search, page, view);
  };

  const fetchProductCount = async function (view) {
    const endpoint =
      view === PRODUCT_VIEWS.deleted ? "archivedProducts" : "products";
    const callResult = await makePostApiCall(endpoint, {
      page: 1,
      search: "",
    });

    if (callResult.success) {
      setProductCounts((prev) => ({
        ...prev,
        [view]: callResult.data?.total ?? 0,
      }));
    }
  };

  const getProducts = async function (
    search,
    page,
    view = PRODUCT_VIEWS.active,
  ) {
    lastFetchRef.current = { search, page, view };
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const endpoint =
      view === PRODUCT_VIEWS.deleted ? "archivedProducts" : "products";
    let callResult = await makePostApiCall(endpoint, payload);
    setLoading(false);
    if (callResult.success) {
      setProducts(callResult.data);
      setProductCounts((prev) => ({
        ...prev,
        [view]: callResult.data?.total ?? 0,
      }));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const deleteProduct = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteProduct/${id}`, {});
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      refreshProducts();
      fetchProductCount(PRODUCT_VIEWS.deleted);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const restoreProduct = async function (id) {
    setRestoringProductId(id);
    let callResult = await makePostApiCall(`restoreProduct/${id}`, {});
    setRestoringProductId(null);

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      refreshProducts();
      fetchProductCount(PRODUCT_VIEWS.active);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Product restored.",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data?.message || "Could not restore product.",
            SnackbarType.Error,
          ),
        );
      }
    }
  };

  const askDeleteProduct = function (product, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteProduct(product.id, index)}
          message={`Are you sure you want to delete ${product.name_en || product.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const askRestoreProduct = function (product) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Restore product"
          buttonMessage="Restore"
          confirmAction={() => restoreProduct(product.id)}
          message={`Restore ${product.name_en || product.name}? This will make the product available again.`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const uploadProductsCsv = async function (uploadedFile) {
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "uploadProductsCsv",
      payload,
      "multipart/form-data",
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      refreshProducts();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const addInventoryApiCall = async function (values) {
    let callResult = await makePostApiCall(
      "addManufacturedProductInventory",
      values,
    );

    if (callResult.success) {
      setSlideOver({
        childComponent: null,
        open: false,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openImportProduct = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadProductsCsv}
          setOpen={setEmptyModelOpen}
          title="Products"
        />
      ),
      open: true,
    });
  };

  const addInventory = () => {
    setSlideOver({
      childComponent: (
        <AddInventoryForm
          actionCall={addInventoryApiCall}
          productOptions={productsForDropdown}
          setOpen={setSlideOver}
        />
      ),
      open: true,
    });
  };

  const fetchProductFromQb = async function (quickbooksId) {
    return makeGetApiCall(`quickbooks/fetchProductFromQb/${quickbooksId}`);
  };

  const openQbLookup = async function (product) {
    if (!product.quickbooks_id) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "This product has no QuickBooks ID.",
          SnackbarType.Error,
        ),
      );
      return;
    }

    setQbLookupLoadingProductId(product.id);
    try {
      const callResult = await fetchProductFromQb(product.quickbooks_id);

      if (callResult.success) {
        const qbProduct = callResult.data?.data ?? callResult.data;
        setSlideOver({
          childComponent: (
            <QbProductLookupPanel product={qbProduct} setOpen={setSlideOver} />
          ),
          open: true,
        });
      } else {
        const errorMessage =
          callResult.data?.subtitle ??
          callResult.data?.message ??
          "Failed to fetch QuickBooks product.";
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            errorMessage,
            SnackbarType.Error,
          ),
        );
      }
    } finally {
      setQbLookupLoadingProductId(null);
    }
  };

  return {
    askDeleteProduct,
    askRestoreProduct,
    emptyModal,
    setEmptyModelOpen,
    getProducts,
    fetchProductCount,
    loading,
    products,
    productCounts,
    restoringProductId,
    setSlideOver,
    slideOver,
    openImportProduct,
    addInventory,
    fetchProductsForDropdown,
    openQbLookup,
    qbLookupLoadingProductId,
  };
}
